import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { HardDrive } from 'lucide-react';
import { useT } from '@/i18n';
import { Empty, ErrorBox, Ltr, QueryView } from '@/components/ui';
import { api } from '@/lib/api/endpoints';
import { qk } from '@/lib/api/keys';
import { useDeviceFiles, useDevices, useWorkspace, useWs } from '@/lib/api/hooks';
import { SOURCE_KINDS } from '@/lib/constants';
import { clipDuration, formatBytes } from '@/lib/format';
import { defaultLabel, guessKind } from '@/lib/upload/plan';
import { logError, logInfo } from '@/lib/log';
import { DeviceStatusPill } from '@/components/devices';
import type { DeviceFile, PipelineSession, SourceKind } from '@/lib/api/types';

interface Choice {
  kind: SourceKind;
  person: string;
  label: string;
}

/**
 * "Files on a studio computer" on the upload page (amendment 14): pick recordings the engine registered on a
 * paired computer. They become sources `device://<id>/<hash>`; nothing is uploaded but proxies, audio and renders.
 */
export function LocalFiles({ session, ensureSession }: { session: PipelineSession | undefined; ensureSession: () => Promise<string | null> }) {
  const { t, td } = useT();
  const qc = useQueryClient();
  const w = useWs();
  const devices = useDevices();
  const ws = useWorkspace();
  const usable = useMemo(() => (devices.data ?? []).filter((d) => d.status !== 'revoked'), [devices.data]);
  const locked = session?.routing?.device?.id ?? null;
  const [deviceId, setDeviceId] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (locked) setDeviceId(locked);
    else if (!deviceId && usable.length) setDeviceId((usable.find((d) => d.online) ?? usable[0]).id);
  }, [locked, deviceId, usable]);
  const files = useDeviceFiles(deviceId);
  const [filter, setFilter] = useState('');
  const [picked, setPicked] = useState<Record<string, Choice>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  if (session?.routing?.cloud_fallback) return null;
  const inSession = new Set((session?.sources ?? []).map((s) => s.content_hash).filter(Boolean));
  const cloudOnly = ws.data?.processingLocation === 'cloud_only';
  const processing = session?.status === 'processing' || session?.status === 'queued';

  const toggle = (f: DeviceFile) =>
    setPicked((p) => {
      const next = { ...p };
      if (next[f.content_hash]) delete next[f.content_hash];
      else next[f.content_hash] = { kind: guessKind({ name: f.filename, type: '' }), person: '', label: defaultLabel(f.filename) };
      return next;
    });
  const setChoice = (hash: string, patch: Partial<Choice>) => setPicked((p) => ({ ...p, [hash]: { ...p[hash], ...patch } }));

  const add = async () => {
    if (!deviceId) return;
    setBusy(true);
    setError(null);
    try {
      const sid = await ensureSession();
      if (!sid) return;
      const list = Object.entries(picked).map(([content_hash, c]) => ({ content_hash, kind: c.kind, person: c.person.trim() || undefined, label: c.label.trim() || undefined }));
      await api.pipeline.attachDeviceSources(sid, deviceId, list);
      logInfo('upload', `attached ${list.length} local file(s) session=${sid} device=${deviceId}`);
      setPicked({});
      await qc.invalidateQueries({ queryKey: qk.session(w, sid) });
      await qc.invalidateQueries({ queryKey: qk.devices(w) });
    } catch (e) {
      logError('upload', `could not attach local files device=${deviceId}`, e);
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const count = Object.keys(picked).length;
  const q = filter.trim().toLowerCase();
  return (
    <section className="card" aria-labelledby="local-h">
      <div className="card-head" style={{ display: 'grid', gap: 2 }}>
        <h2 id="local-h" className="h2 row" style={{ gap: 8 }}><HardDrive size={16} aria-hidden />{t('localFilesTitle')}</h2>
        <span className="muted small">{t('localFilesSub')}</span>
      </div>
      {cloudOnly ? <div className="alert alert-info" style={{ margin: 16 }}>{t('localCloudOnly')}</div> : (
        <QueryView query={devices}>
          {() =>
            usable.length === 0 ? (
              <Empty>{t('localNoComputer')}</Empty>
            ) : (
              <div className="stack" style={{ gap: 0 }}>
                <div className="row wrap" style={{ gap: 10, padding: '10px 16px' }}>
                  <label className="row small" style={{ gap: 6 }}>
                    {t('localPickComputer')}
                    <select className="select" style={{ height: 32 }} value={deviceId ?? ''} disabled={!!locked} onChange={(e) => { setDeviceId(e.target.value); setPicked({}); }}>
                      {usable.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </label>
                  {deviceId ? <DeviceStatusPill status={usable.find((d) => d.id === deviceId)?.status ?? 'offline'} /> : null}
                  <input className="input push" style={{ height: 32, maxWidth: 260 }} placeholder={t('localFilter')} aria-label={t('localFilter')} value={filter} onChange={(e) => setFilter(e.target.value)} />
                </div>
                <QueryView query={files}>
                  {(list) => {
                    const shown = list.filter((f) => !inSession.has(f.content_hash) && (!q || f.path.toLowerCase().includes(q)));
                    if (list.length === 0) return <Empty>{t('localNoFiles')}</Empty>;
                    return (
                      <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                        {shown.map((f) => {
                          const c = picked[f.content_hash];
                          return (
                            <div key={f.content_hash} className="card-row" style={{ display: 'grid', gap: 6, padding: '8px 16px' }}>
                              <label className="row" style={{ gap: 10, cursor: 'pointer' }}>
                                <input type="checkbox" checked={!!c} disabled={processing} onChange={() => toggle(f)} aria-label={f.filename} />
                                <span className="stack" style={{ gap: 0, minWidth: 0 }}>
                                  <Ltr className="mono ellipsis"><span style={{ fontSize: 12, fontWeight: 500 }}>{f.filename}</span></Ltr>
                                  <Ltr className="muted ellipsis"><span style={{ fontSize: 11 }}>{f.path}</span></Ltr>
                                </span>
                                <span className="muted small push">
                                  {f.media_info.duration_ms ? <Ltr className="mono">{clipDuration(f.media_info.duration_ms)}</Ltr> : null} · {formatBytes(f.size)}
                                </span>
                              </label>
                              {c ? (
                                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(110px, 140px) minmax(0,1fr) minmax(0,1fr)', gap: 8, paddingInlineStart: 26 }}>
                                  <select className="select" style={{ height: 30 }} aria-label={`${t('fileKind')} · ${f.filename}`} value={c.kind} onChange={(e) => setChoice(f.content_hash, { kind: e.target.value as SourceKind })}>
                                    {SOURCE_KINDS.map((k) => <option key={k} value={k}>{td(`kind_${k}`)}</option>)}
                                  </select>
                                  <input className="input" style={{ height: 30 }} placeholder={t('filePersonPh')} aria-label={`${t('filePerson')} · ${f.filename}`} value={c.person} onChange={(e) => setChoice(f.content_hash, { person: e.target.value })} />
                                  <input className="input" style={{ height: 30 }} placeholder={t('fileLabel')} aria-label={`${t('fileLabel')} · ${f.filename}`} value={c.label} onChange={(e) => setChoice(f.content_hash, { label: e.target.value })} />
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    );
                  }}
                </QueryView>
                {error ? <div style={{ padding: '0 16px 12px' }}><ErrorBox error={error} /></div> : null}
                {count ? (
                  <div className="row" style={{ padding: '12px 16px' }}>
                    <button type="button" className="btn btn-inv push" disabled={busy || processing} onClick={() => void add()}>{count === 1 ? t('localAddOne') : t('localAdd', { n: count })}</button>
                  </div>
                ) : null}
              </div>
            )
          }
        </QueryView>
      )}
    </section>
  );
}
